import React from 'react';
import { Link } from 'react-router-dom';
import './TermsOfService.css';

const COLORS = {
  background: '#FAF7F2',
  labels: '#4A4A4A',
  shadows: '#8B857A',
  romantic: '#E6B3C2',
  borders: '#C5BBB0',
};

export default function TermsOfService() {
  return (
    <div className="terms-container" style={{ backgroundColor: COLORS.background, minHeight: '100vh', padding: '2rem' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-block mb-4 text-sm hover:underline"
            style={{ color: COLORS.shadows }}
          >
            ← Back to Map
          </Link>
          <h1 className="text-4xl font-bold mb-2" style={{ color: COLORS.labels }}>
            Terms of Service
          </h1>
          <p className="text-sm" style={{ color: COLORS.shadows }}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="terms-content" style={{ color: COLORS.labels, lineHeight: '1.8' }}>
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>1. Acceptance of Terms</h2>
            <p className="mb-4">
              By accessing and using Velvynote ("the Service"), you accept and agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
            <p>
              We reserve the right to modify these terms at any time. Your continued use of the Service after any changes 
              constitutes acceptance of the modified terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>2. Description of Service</h2>
            <p className="mb-4">
              Velvynote is a location-based memory sharing platform that allows users to create and share digital memories 
              (post-it notes) at specific geographic locations on an interactive map.
            </p>
            <p>
              The Service uses vector tile technology to display memories efficiently at various zoom levels, enabling 
              users to discover and share meaningful moments tied to physical places.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>3. User Accounts</h2>
            <p className="mb-4">
              You may use the Service with or without creating an account. When creating an account, you agree to:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Provide accurate and complete information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized access</li>
              <li>Be responsible for all activities under your account</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these terms or engage in harmful activities.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>4. User Content and Conduct</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>4.1 Content Guidelines</h3>
            <p className="mb-4">
              You retain ownership of any content you post ("User Content"). By posting content, you grant us a 
              non-exclusive, worldwide, royalty-free license to use, display, and distribute your content as necessary 
              to operate the Service.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>4.2 Prohibited Content</h3>
            <p className="mb-2">You agree not to post content that:</p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Is illegal, harmful, or violates any applicable laws</li>
              <li>Infringes on intellectual property, privacy, or other rights</li>
              <li>Contains hate speech, harassment, or discriminatory language</li>
              <li>Is pornographic, sexually explicit, or obscene</li>
              <li>Contains spam, scams, or fraudulent content</li>
              <li>Violates privacy or contains personally identifiable information of others without consent</li>
              <li>Impersonates any person or entity</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>4.3 Content Moderation</h3>
            <p className="mb-4">
              Velvynote employs both automated filters and asynchronous human review to moderate content.
              Certain content may be blocked automatically at the time of submission. Additionally, all user-generated
              content is subject to ongoing review and may be moderated after publication.
            </p>
            <p className="mb-4">
              We reserve the right to, at any time and without prior notice:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Review, modify, or remove any content that violates these terms or community guidelines</li>
              <li>Suspend or permanently ban accounts that violate these terms, including retrospectively for past violations</li>
              <li>Remove content that we determine, in our sole discretion, to be harmful, inappropriate, or in violation of applicable laws</li>
              <li>Retain records of removed content and moderation actions for safety and legal compliance purposes</li>
            </ul>
            <p className="mb-4">
              By using the Service, you acknowledge that content moderation may occur asynchronously and that content
              visible on the platform at any given time may not yet have been reviewed. The presence of content on the
              platform does not constitute our endorsement or approval of that content.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>5. Location Data</h2>
            <p className="mb-4">
              The Service collects and stores geographic coordinates (latitude and longitude) for memories you create. 
              This location data is essential for displaying memories on the map.
            </p>
            <p>
              By using the Service, you consent to the collection and processing of location data as described in our 
              Privacy Policy. You should not post memories at locations where you do not want to reveal your presence.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>6. Anonymous Use</h2>
            <p className="mb-4">
              The Service allows anonymous posting of memories. Anonymous memories are linked to a temporary identifier 
              and may have limited functionality (e.g., deletion) compared to authenticated memories.
            </p>
            <p>
              Even when posting anonymously, you must still comply with all terms and content guidelines. We may still 
              be able to identify and take action against anonymous accounts that violate these terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>7. Intellectual Property</h2>
            <p className="mb-4">
              The Service, including its design, features, technology, and underlying code, is owned by Velvynote and 
              protected by intellectual property laws. You may not:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Copy, modify, or create derivative works of the Service</li>
              <li>Reverse engineer or attempt to extract source code</li>
              <li>Use the Service for commercial purposes without permission</li>
              <li>Remove or alter any copyright, trademark, or proprietary notices</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>8. Privacy</h2>
            <p className="mb-4">
              Your privacy is important to us. Our collection and use of personal information is governed by our 
              Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy 
              to understand how we handle your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>9. Service Availability</h2>
            <p className="mb-4">
              We strive to provide reliable service but do not guarantee uninterrupted or error-free operation. The 
              Service may be temporarily unavailable due to maintenance, updates, or unforeseen circumstances.
            </p>
            <p>
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time with or 
              without notice.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>10. Disclaimers and Limitations</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>10.1 Service Provided "As Is"</h3>
            <p className="mb-4">
              The Service is provided "as is" and "as available" without warranties of any kind, either express or 
              implied. We disclaim all warranties, including but not limited to merchantability, fitness for a 
              particular purpose, and non-infringement.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>10.2 Limitation of Liability</h3>
            <p className="mb-4">
              To the maximum extent permitted by law, Velvynote and its affiliates shall not be liable for any 
              indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, 
              whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-4" style={{ color: COLORS.labels }}>10.3 Content Accuracy</h3>
            <p>
              We do not verify the accuracy, completeness, or legitimacy of user-generated content. You use the 
              Service and view content at your own risk.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>11. Indemnification</h2>
            <p className="mb-4">
              You agree to indemnify and hold harmless Velvynote, its officers, directors, employees, and agents from 
              any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of:
            </p>
            <ul className="list-disc pl-6 mb-4 space-y-2">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Your User Content</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>12. Termination</h2>
            <p className="mb-4">
              You may stop using the Service at any time. We reserve the right to suspend or terminate your access 
              to the Service immediately, without prior notice, for any violation of these Terms or for any other 
              reason we deem necessary.
            </p>
            <p>
              Upon termination, your right to use the Service will cease immediately. We may delete your account and 
              content at our discretion, subject to applicable data retention laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>13. Governing Law</h2>
            <p className="mb-4">
              These Terms shall be governed by and construed in accordance with the laws of [Your Jurisdiction], 
              without regard to its conflict of law provisions.
            </p>
            <p>
              Any disputes arising from these Terms or the Service shall be resolved in the courts of [Your Jurisdiction].
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>14. Contact Information</h2>
            <p className="mb-4">
              If you have questions about these Terms of Service, please contact us at:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:hello@velvynote.com" style={{ color: COLORS.romantic }}>hello@velvynote.com</a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4" style={{ color: COLORS.labels }}>15. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited 
              or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section className="mb-8 pt-8 border-t" style={{ borderColor: COLORS.borders }}>
            <p className="text-sm italic" style={{ color: COLORS.shadows }}>
              By using Velvynote, you acknowledge that you have read, understood, and agree to be bound by these 
              Terms of Service.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

